import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  CharterCapitalDto,
  CharterHolderDto,
  CharterHoldersDisclosure,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CharterHistoryListComponent } from '@features/dashboard/company/corporate-charter/components/charter-history-list/charter-history-list.component';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  capitalHistoryRows,
  dominantSource,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import {
  SHARE_CLASS_LABEL,
  formatMoney,
  formatPercent,
  formatUnits,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/** Cores da barra de participação, na ordem do quadro. */
const SLICE_COLORS = ['#0b1730', '#00a819', '#f59e0b', '#0ea5e9', '#a855f7', '#64748b'];

/**
 * Cartão "Capital social": capital subscrito, quotas ou ações (por classe na S.A.), integralização, capital
 * autorizado e, quando o quadro é completo, a barra de participação dos sócios (percentuais do backend). "Vigente desde
 * <ato>" e **Ver histórico** mostram o capital como cada ato o descreveu, com a procedência.
 */
@Component({
  selector: 'app-charter-capital-card',
  imports: [CharterProvenanceComponent, CharterHistoryListComponent],
  templateUrl: './charter-capital-card.component.html',
  styleUrl: './charter-capital-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterCapitalCardComponent {
  readonly capital = input.required<CharterCapitalDto>();
  readonly holders = input<CharterHolderDto[]>([]);
  readonly holdersStatus = input<CharterHoldersDisclosure>('not-disclosed');

  protected readonly formatPercent = formatPercent;
  protected readonly historyOpen = signal(false);

  protected readonly history = computed(() =>
    capitalHistoryRows(
      this.capital().history ?? [],
      this.capital().unitKind,
      this.capital().currency
    )
  );

  /** Fonte dominante do cartão: a do capital social, das quotas e do valor unitário. */
  protected readonly source = computed(() => {
    const c = this.capital();
    return dominantSource([c.shareCapital, c.totalUnits, c.unitValue, c.paidInCapital]);
  });

  protected readonly total = computed(() =>
    formatMoney(this.capital().shareCapital.value, this.capital().currency)
  );

  /** "1.000.000 quotas de R$ 1,00" ou "50.000.000 ações sem valor nominal". */
  protected readonly unitsLine = computed(() => {
    const c = this.capital();
    if (c.totalUnits.value == null) {
      return null;
    }
    const noun = c.unitKind === 'shares' ? 'ações' : 'quotas';
    const units = `${formatUnits(c.totalUnits.value)} ${noun}`;
    if (c.hasParValue.value === false) {
      return `${units} sem valor nominal`;
    }
    return c.unitValue.value != null
      ? `${units} de ${formatMoney(c.unitValue.value, c.currency)}`
      : units;
  });

  /** "totalmente integralizado" ou o valor integralizado, quando o documento diz. */
  protected readonly paidInLine = computed(() => {
    const c = this.capital();
    if (c.paidInCapital.value == null) {
      return null;
    }
    if (c.shareCapital.value != null && c.paidInCapital.value === c.shareCapital.value) {
      return 'totalmente integralizado';
    }
    return `${formatMoney(c.paidInCapital.value, c.currency)} integralizado`;
  });

  protected readonly classes = computed(() =>
    this.capital().shareClasses.map(shareClass => ({
      label: shareClass.label || SHARE_CLASS_LABEL[shareClass.class],
      units: formatUnits(shareClass.units.value),
      evidence: shareClass.units,
      votingRight: shareClass.votingRight,
      preferences: shareClass.preferences,
    }))
  );

  protected readonly authorized = computed(() => {
    const c = this.capital();
    if (c.authorizedCapital.value == null && c.authorizedCapitalUnits.value == null) {
      return null;
    }
    const noun = c.unitKind === 'shares' ? 'ações' : 'quotas';
    const parts = [
      c.authorizedCapital.value != null
        ? `até ${formatMoney(c.authorizedCapital.value, c.currency)}`
        : null,
      c.authorizedCapitalUnits.value != null
        ? `${formatUnits(c.authorizedCapitalUnits.value)} ${noun}`
        : null,
    ];
    return {
      text: parts.filter(Boolean).join(' · '),
      evidence: c.authorizedCapital.value != null ? c.authorizedCapital : c.authorizedCapitalUnits,
    };
  });

  /** Fatias da barra: só com quadro completo, cada uma com o % que o backend devolveu. */
  protected readonly slices = computed(() => {
    if (this.holdersStatus() !== 'full') {
      return [];
    }
    return this.holders()
      .filter(holder => holder.percentage.value != null && holder.percentage.value > 0)
      .map((holder, index) => ({
        name: holder.name,
        percentage: holder.percentage.value as number,
        color: SLICE_COLORS[index % SLICE_COLORS.length],
      }));
  });
}
